import * as React from 'react'
import { Descriptions, Tag } from 'antd'
import { Well } from '@pushly/aqe/lib/components'
import { IPageAnalysis, IPageCategory } from '@pushly/aqe/lib/interfaces'
import { getFriendlyPathName } from '@pushly/aqe/lib/utils'

const STANDARD_FIELDS: Array<keyof IPageAnalysis> = [
    'canonical_url',
    'type',
    'title',
    'description',
    'image_url',
    'keywords',
    'published_date',
]

interface IProps {
    pageAnalysis: IPageAnalysis
    isPartial?: boolean
    pageCategories: IPageCategory[]
}

/**
 * Renders the standard top-level results of a page analysis run: the
 * known-shape fields, dynamic additional_fields, and resolved page_categories.
 */
function PageAnalysisResultsPanel(props: IProps) {
    const { pageAnalysis, isPartial, pageCategories } = props
    const rows = buildRows(pageAnalysis, !!isPartial, pageCategories)

    return (
        <Well title="Page Analysis Results" footerMode="hidden">
            <Descriptions layout="horizontal" column={1} colon={false}>
                {rows.map((row) => (
                    <Descriptions.Item key={row.label} label={row.label}>
                        {row.value}
                    </Descriptions.Item>
                ))}
            </Descriptions>
        </Well>
    )
}

function buildRows(
    pa: IPageAnalysis,
    isPartial: boolean,
    pageCategories: IPageCategory[],
): Array<{ label: string; value: React.ReactNode }> {
    const rows: Array<{ label: string; value: React.ReactNode }> = []

    for (const field of STANDARD_FIELDS) {
        const value = pa[field]
        if (isPartial && !value) continue

        if (field === 'keywords') {
            rows.push({
                label: field,
                value: ((value as string[]) || []).map((k, idx) => <Tag key={idx}>{k}</Tag>),
            })
        } else {
            rows.push({ label: field, value })
        }
    }

    if (pa.additional_fields && typeof pa.additional_fields === 'object') {
        for (const key of Object.keys(pa.additional_fields)) {
            rows.push({
                label: `additional_fields.${key}`,
                value: pa.additional_fields[key],
            })
        }
    }

    rows.push({
        label: 'page_categories',
        value: ((pa.page_categories as number[]) || []).map((pcId, idx) => (
            <Tag key={idx}>
                {getFriendlyPathName(
                    pageCategories,
                    pageCategories.find((pc) => pc.id === pcId),
                )}
            </Tag>
        )),
    })

    return rows
}

export default PageAnalysisResultsPanel
